export type ResidentStatus =
  | "active"
  | "discharged"
  | "deceased"
  | "inactive"
  | string;

export interface ResidentFamilyMember {
  id?: number;
  name: string;
  relationship?: number | null;
  relationship_name?: string;
  relationship_code?: string;
  mobile?: string;
  alt_mobile?: string;
  email?: string;
  date_of_birth?: string;
  occupation?: string;
  district?: number | null;
  city?: number | null;
  area?: number | null;
  district_name?: string;
  city_name?: string;
  area_name?: string;
  address?: string;
  is_primary_contact?: boolean;
  is_emergency_contact?: boolean;
}

export interface ResidentFamilyMemberPayload {
  id?: number;
  name: string;
  relationship?: number;
  mobile?: string;
  alt_mobile?: string;
  email?: string;
  date_of_birth?: string;
  occupation?: string;
  district?: number;
  city?: number;
  area?: number;
  address?: string;
  is_primary_contact?: boolean;
  is_emergency_contact?: boolean;
}

export interface ResidentPayload {
  full_name: string;
  gender?: string;
  date_of_birth?: string;
  blood_group?: string;
  mobile?: string;
  aadhaar?: string;
  district?: number;
  city?: number;
  area?: number;
  pincode?: string;
  religion?: string;
  jain_status?: string;
  marital_status?: string;
  admission_date?: string;
  admission_form_no?: string;
  admission_type?: string;
  monthly_maintenance?: string;
  room_no?: string;
  category?: string;
  status?: ResidentStatus;
  previous_address?: string;
  reason_for_admission?: string;
  notes?: string;
  family_members?: ResidentFamilyMemberPayload[];
}

export interface ResidentRecord
  extends Omit<ResidentPayload, "family_members"> {
  id: number;
  resident_code?: string;
  district_name?: string;
  city_name?: string;
  area_name?: string;
  family_members?: ResidentFamilyMember[];
  created_at?: string;
  updated_at?: string;
}

export type Resident = Pick<
  ResidentRecord,
  "id" | "full_name" | "resident_code" | "created_at"
>;

export interface ResidentStats {
  total: number;
  active: number;
  discharged: number;
  deceased: number;
}

export const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
] as const;

export const JAIN_STATUS_OPTIONS = [
  { value: "shwetambar", label: "Shwetambar" },
  { value: "digambar", label: "Digambar" },
  { value: "sthanakvasi", label: "Sthanakvasi" },
  { value: "other", label: "Other" },
] as const;

export const MARITAL_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "widowed", label: "Widowed" },
  { value: "divorced", label: "Divorced" },
] as const;

export const ADMISSION_TYPE_OPTIONS = [
  { value: "permanent", label: "Permanent" },
  { value: "temporary", label: "Temporary" },
] as const;

export const CATEGORY_OPTIONS = [
  { value: "general", label: "General" },
  { value: "bpl", label: "BPL" },
  { value: "other", label: "Other" },
] as const;

export const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "discharged", label: "Discharged" },
  { value: "deceased", label: "Deceased" },
  // { value: "inactive", label: "Inactive" },
] as const;

export function normalizeResidentStatus(
  status: ResidentStatus | undefined
): "active" | "discharged" | "deceased" | "inactive" {
  const value = String(status ?? "").toLowerCase();
  if (value === "discharged") {
    return "discharged";
  }
  if (value === "deceased") {
    return "deceased";
  }
  if (value === "inactive") {
    return "inactive";
  }
  return "active";
}
